import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get } from "../lib/api";
import { Seo, breadcrumbJsonLd } from "../lib/seo";
import { Section, SectionHead, Breadcrumb, Button } from "../components/ui";
import { DOCS_URL } from "../data/nav";
import { useReveal } from "../lib/useReveal";

export function IntegrationsDir() {
  const { data } = useQuery({ queryKey: ["integrations-dir"], queryFn: () => get("/integrations-directory") });
  const [cat, setCat] = useState("");
  const items = data || [];
  const cats = [...new Set(items.map((i) => i.category))];
  const filtered = cat ? items.filter((i) => i.category === cat) : items;
  useReveal(filtered);
  return (
    <>
      <Seo title="Integrations" path="/integrations/" description="Connect BloomCONNECT to Zapier, Make, n8n, GoHighLevel, Shopify, WooCommerce, Google Calendar, Twilio and more." />
      <Section>
        <SectionHead label="Integrations" title="Connect BloomCONNECT to your stack." />
        <div className="mt-8 flex flex-wrap gap-2">
          <button onClick={() => setCat("")} className={`badge ${!cat ? "!bg-ink-950 !text-white !border-ink-950" : ""}`}>All</button>
          {cats.map((c) => <button key={c} onClick={() => setCat(c)} className={`badge ${cat === c ? "!bg-ink-950 !text-white !border-ink-950" : ""}`}>{c}</button>)}
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((i) => (
            <Link key={i.slug} to={`/integrations/${i.slug}/`} className="card-hairline p-6 hover:border-brand-600 reveal-on-scroll">
              <div className="eyebrow">{i.category}</div>
              <h3 className="mt-2 text-lg text-ink-950">{i.name}</h3>
              <p className="mt-2 text-sm text-ink-600">{i.description}</p>
            </Link>
          ))}
        </div>
      </Section>
    </>
  );
}

export function IntegrationDetail() {
  const { slug } = useParams();
  const { data } = useQuery({ queryKey: ["integrations-dir"], queryFn: () => get("/integrations-directory") });
  const i = (data || []).find((x) => x.slug === slug);
  return (
    <>
      <Seo title={i ? i.name : "Integration"} path={`/integrations/${slug}/`} description={i ? `Connect ${i.name} to BloomCONNECT. ${i.description}` : ""} jsonLd={breadcrumbJsonLd([{name:"Integrations",path:"/integrations/"},{name:i?.name||slug,path:`/integrations/${slug}/`}])} />
      <Section>
        <Breadcrumb items={[{name:"Integrations",path:"/integrations/"},{name:i?.name||slug}]} />
        {i ? (
          <div className="mt-6 max-w-prose">
            <div className="eyebrow">{i.category}</div>
            <h1 className="mt-2 text-4xl md:text-5xl tracking-tight text-ink-950">{i.name} + BloomCONNECT</h1>
            <p className="mt-5 text-lg text-ink-600">{i.description}</p>
            <h2 className="mt-10 text-xl text-ink-950">How to connect</h2>
            <ol className="mt-4 space-y-2 text-ink-600 list-decimal pl-5">
              <li>Open Integrations in your BloomCONNECT workspace.</li>
              <li>Select {i.name} and authorise the connection.</li>
              <li>Map the events or fields you want to sync.</li>
              <li>Save and test with a real conversation.</li>
            </ol>
            <div className="mt-8 flex gap-3"><Button href={DOCS_URL} variant="secondary" size="md">Read the docs</Button><Button to="/pricing/" variant="primary" size="md" data-testid="integrations-see-pricing-btn">See Pricing</Button></div>
          </div>
        ) : <p className="mt-8 text-ink-400">Integration not found.</p>}
      </Section>
    </>
  );
}
